import React, { useContext } from "react";
import { func, shape, number } from "prop-types";
import { Link as ReactRouterLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExpandAlt } from "@fortawesome/free-solid-svg-icons";
import { useOffline } from "../../../contexts/OfflineContext";

import NotificationContext from "../../../contexts/NotificationsContext";
import Popper from "../Popper/Popper";
import NotificationList from "./NotificationList";

/**
 * The notification bell's popper.
 *
 * Presentation only. Navigation, the expanded modal and the clear-all
 * confirmation are owned by `NotificationsCenter`, because this popper closes on
 * mouse leave and would take any modal of its own down with it.
 *
 * SW2-2180's division resolution moved with the rest of the navigation, into
 * `useNotificationNavigate`.
 */
function NotificationPopper(props) {
  const notificationContext = useContext(NotificationContext);
  const isOffline = useOffline();

  const hasNotifications = notificationContext.notifications.length > 0;

  const Link = ({ children, ...rest }) => {
    // Create a handler that prevents navigation when offline
    const handleClick = (e) => {
      if (isOffline) {
        e.preventDefault();

        return;
      }

      if (props.handleClose) {
        props.handleClose(e);
      }
    };

    return (
      <ReactRouterLink
        {...rest}
        onClick={handleClick}
      >
        {children}
      </ReactRouterLink>
    );
  };

  const disabledStyle = isOffline
    ? { opacity: 0.6, cursor: "not-allowed" }
    : {};

  return (
    <Popper
      classes="notification-menu"
      coordinates={props.coordinates}
      handleClose={props.handleClose}
    >
      <div className="header">
        <h3>Notifications</h3>

        {/* Its own header zone, so it centres against the popper rather than
            floating wherever the trailing links leave room */}
        {props.onExpand && (
          <button
            type="button"
            className="notification-expand pointer"
            title="Expand notifications"
            aria-label="Expand notifications"
            onClick={props.onExpand}
          >
            <FontAwesomeIcon icon={faExpandAlt} />
          </button>
        )}

        <span className="notification-actions">
          {hasNotifications && !isOffline && props.onClearAll && (
            <button
              type="button"
              className="notification-action-link"
              onClick={props.onClearAll}
            >
              Clear all
            </button>
          )}
          <Link className="no-underline" to="/my-profile" style={disabledStyle}>
            View Settings
          </Link>
        </span>
      </div>

      <NotificationList
        notifications={notificationContext.notifications}
        onSelect={props.onSelect}
        variant="compact"
      />
    </Popper>
  );
}

NotificationPopper.propTypes = {
  handleClose: func.isRequired,
  onSelect: func.isRequired,
  onExpand: func,
  onClearAll: func,
  coordinates: shape({
    top: number,
    bottom: number,
    left: number,
    right: number,
  }).isRequired,
};

export default NotificationPopper;
